import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageFlag from '../common/LanguageFlag';
import PdfPickerField from '../common/PdfPickerField';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ScrollableDialog from '../common/ScrollableDialog';
import { validateConsentForm } from './consentFormValidation';
import { SUPPORTED_LANGUAGE_CODES } from '../../constants/Languages';
import { MAX_CONSENT_FORM_VALIDITY_MONTHS } from '../../services/consentFormService';
import { BRAND, BRAND_RADIUS } from '../../theme/brandTokens';

const buildLocalizedFields = (languages, values = {}) => (
  Object.fromEntries(languages.map((language) => [language, values?.[language] || '']))
);

const buildInitialForm = (languages, consentForm) => ({
  name: buildLocalizedFields(languages, consentForm?.name),
  validityMonths: consentForm?.validityMonths ?? '',
  file: null,
});

export const buildInputSx = (hasError = false) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: BRAND_RADIUS.button,
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: BRAND.surface,
  boxSizing: 'border-box',
  lineHeight: 'normal',
  minHeight: 48,
  '&:focus': {
    borderColor: BRAND.accent,
  },
});

const FieldMessage = ({ error, helpText }) => {
  const { t } = useTranslation();

  if (error) {
    return (
      <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
        {t(error)}
      </Typography>
    );
  }

  return helpText ? (
    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
      {helpText}
    </Typography>
  ) : null;
};

/** Creates a consent form with its first PDF, or edits the name and validity of an existing one. */
const ConsentFormEditorDialog = ({
  open,
  mode,
  consentForm,
  supportedLanguages,
  saving,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const isCreate = mode !== 'edit';
  const languagesKey = (supportedLanguages?.length ? supportedLanguages : ['en']).join(',');
  const languages = useMemo(() => languagesKey.split(','), [languagesKey]);
  const sortedLanguages = useMemo(
    () => [...languages].sort((a, b) => SUPPORTED_LANGUAGE_CODES.indexOf(a) - SUPPORTED_LANGUAGE_CODES.indexOf(b)),
    [languages],
  );
  const [formData, setFormData] = useState(() => buildInitialForm(languages, consentForm));
  const [errors, setErrors] = useState({});
  const [activeLanguage, setActiveLanguage] = useState(() => sortedLanguages[0] || 'en');

  useEffect(() => {
    if (open) {
      setFormData(buildInitialForm(languages, consentForm));
      setErrors({});
      setActiveLanguage(sortedLanguages[0] || 'en');
    }
  }, [open, consentForm, languages, sortedLanguages]);

  const handleSubmit = () => {
    const result = validateConsentForm(formData, languages, { requireFile: isCreate });
    setErrors(result.errors);
    if (!result.isValid) {
      return;
    }

    onSave(formData);
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      title={isCreate ? t('consentForms.management.dialog.createTitle') : t('consentForms.management.dialog.editTitle')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button id="consent-form-editor-cancel" onClick={onClose} disabled={saving} color="inherit">
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton id="consent-form-editor-save" onClick={handleSubmit} disabled={saving}>
            {saving
              ? t('common.saving')
              : isCreate ? t('consentForms.management.dialog.create') : t('consentForms.management.dialog.save')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box id="consent-form-editor" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ border: '1px solid', borderColor: errors.name ? 'error.main' : 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={activeLanguage}
            onChange={(_, nextLanguage) => setActiveLanguage(nextLanguage)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              backgroundColor: BRAND.background,
              '& .MuiTab-root': { color: BRAND.textMuted, fontWeight: 600, textTransform: 'none', minHeight: 48 },
              '& .Mui-selected': { color: BRAND.accentText },
              '& .MuiTabs-indicator': { backgroundColor: BRAND.accent },
            }}
          >
            {sortedLanguages.map((language) => (
              <Tab
                key={language}
                value={language}
                label={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <LanguageFlag languageCode={language} size="1rem" />
                    <Typography variant="body2">{t(`common.languages.${language}`)}</Typography>
                  </Box>
                )}
              />
            ))}
          </Tabs>
          <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Typography component="label" htmlFor="consent-form-name" variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'block' }}>
              {`${t('consentForms.management.dialog.name')} (${t(`common.languages.${activeLanguage}`)}) *`}
            </Typography>
            <Box
              component="input"
              id="consent-form-name"
              type="text"
              maxLength={200}
              value={formData.name?.[activeLanguage] || ''}
              onChange={(event) => setFormData((previous) => ({
                ...previous,
                name: { ...previous.name, [activeLanguage]: event.target.value },
              }))}
              disabled={saving}
              sx={buildInputSx(Boolean(errors.name))}
            />
            <FieldMessage error={errors.name} />
          </Box>
        </Box>

        <Box>
          <Typography component="label" htmlFor="consent-form-validity" variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'block' }}>
            {t('consentForms.management.dialog.validityMonths')}
          </Typography>
          <Box
            component="input"
            id="consent-form-validity"
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_CONSENT_FORM_VALIDITY_MONTHS}
            step={1}
            placeholder={t('consentForms.management.dialog.validityPlaceholder')}
            value={formData.validityMonths}
            onChange={(event) => setFormData((previous) => ({ ...previous, validityMonths: event.target.value }))}
            disabled={saving}
            sx={buildInputSx(Boolean(errors.validityMonths))}
          />
          <FieldMessage error={errors.validityMonths} helpText={t('consentForms.management.dialog.validityHelp')} />
        </Box>

        {isCreate ? (
          <>
            <PdfPickerField
              id="consent-form-file"
              label={`${t('consentForms.management.dialog.file')} *`}
              file={formData.file}
              onChange={(file) => setFormData((previous) => ({ ...previous, file }))}
              error={errors.file}
              chooseLabel={t('consentForms.management.upload.choose')}
              replaceLabel={t('consentForms.management.upload.replace')}
              disabled={saving}
            />
            <Alert severity="info" id="consent-form-first-version-note">
              {t('consentForms.management.upload.firstVersionNote')}
            </Alert>
          </>
        ) : null}
      </Box>
    </ScrollableDialog>
  );
};

export default ConsentFormEditorDialog;
